// ─────────────────────────────────────────────
//  MessageComposer — the chat input bar (Phase 5 messaging / UI-SPEC
//  "Composer"). Extends the MessagesPage inputBar/msgInput/sendBtn pattern,
//  lifted to the token contract and upgraded to a multi-line-friendly textarea.
//
//  • Enter sends; Shift+Enter inserts a newline (FLAG-3).
//  • Send is disabled while the trimmed body is empty or a send is in flight;
//    while sending the label is "…" with aria-busy.
//  • A visually-hidden <label htmlFor> binds the textarea for screen readers.
//  • readOnlyReason → the composer is disabled and a helper line explains why
//    ("You can read this channel but can't post here."). The SERVER rule is the
//    real boundary (D-10 / T-05-15); this is only a UI hint.
//  • 44×44 min hit areas; focus-visible 2px navy ring.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import tokens from "../theme/tokens";

const { color, font, space, radius } = tokens;

const FOCUS_RING = "0 0 0 2px #1e40af";

export default function MessageComposer({ onSend, disabled, readOnlyReason }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [focused, setFocused] = useState(false);

  const readOnly = !!readOnlyReason;
  const trimmed = body.trim();
  const canSend = !disabled && !readOnly && !sending && trimmed.length > 0;

  const submit = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await onSend(trimmed);
      setBody("");
    } catch (err) {
      // The caller (ConversationView) surfaces the toast; keep the draft so the
      // user can retry without retyping.
      console.error("Composer send failed:", err);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
    // Shift+Enter falls through → the textarea inserts a newline.
  };

  return (
    <div style={styles.bar}>
      <label htmlFor="message-composer-input" style={styles.hiddenLabel}>
        Type a message
      </label>
      <textarea
        id="message-composer-input"
        rows={1}
        style={{
          ...styles.input,
          ...(focused ? { boxShadow: FOCUS_RING, borderColor: color.accent.primary } : {}),
          ...(disabled || readOnly ? styles.inputDisabled : {}),
        }}
        value={body}
        disabled={disabled || readOnly}
        placeholder={readOnly ? "Read-only channel" : "Type a message…"}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        aria-describedby={readOnly ? "message-composer-helper" : undefined}
      />
      <button
        type="button"
        style={{ ...styles.sendBtn, ...(canSend ? {} : styles.sendBtnDisabled) }}
        onClick={submit}
        disabled={!canSend}
        aria-busy={sending ? "true" : undefined}
        aria-label="Send"
      >
        {sending ? "…" : "Send"}
      </button>
      {readOnly && (
        <div id="message-composer-helper" style={styles.helper}>
          {readOnlyReason}
        </div>
      )}
    </div>
  );
}

const styles = {
  bar: {
    position: "relative",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    gap: space.sm,
    padding: space.md,
    borderTop: `1px solid ${color.border.default}`,
    background: color.surface.card,
    flexShrink: 0,
  },
  hiddenLabel: {
    position: "absolute",
    width: 1,
    height: 1,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
  },
  input: {
    flex: 1,
    minWidth: 0,
    minHeight: 44, // 44px touch target
    maxHeight: 120,
    resize: "none",
    background: color.surface.raised,
    border: `1px solid ${color.border.default}`,
    color: color.text.primary,
    borderRadius: radius.full,
    padding: "11px 16px",
    fontSize: font.size.body, // 14
    fontFamily: font.family,
    lineHeight: font.lineHeight.normal,
    boxSizing: "border-box",
    outline: "none",
  },
  inputDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  sendBtn: {
    minWidth: 64,
    minHeight: 44, // 44px touch target
    background: color.accent.primary, // navy #1e40af
    color: "#fff",
    border: "none",
    borderRadius: radius.sm,
    padding: "0 16px",
    fontSize: font.size.body, // 14
    fontWeight: font.weight.bold,
    cursor: "pointer",
    flexShrink: 0,
  },
  sendBtnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  helper: {
    flexBasis: "100%",
    fontSize: font.size.label, // 12
    color: color.text.muted,
    marginTop: space.xs,
  },
};
